import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import VendedorDashboard from "@/pages/VendedorDashboard";
import MiDiaPage from "@/pages/MiDiaPage";
import OperacionesPage from "@/pages/OperacionesPage";
import DirectorPage from "@/pages/DirectorPage";
import RemisionesPage from "@/pages/RemisionesPage";
import TeamPage from "@/pages/TeamPage";
import ClientesPage from "@/pages/ClientesPage";
import ProductosPage from "@/pages/ProductosPage";
import PedidosPage from "@/pages/PedidosPage";
import ListasPreciosPage from "@/pages/ListasPreciosPage";
import ExcepcionesPage from "@/pages/ExcepcionesPage";
import ComisionesPage from "@/pages/ComisionesPage";
import NotificacionesPage from "@/pages/NotificacionesPage";
import ReportesPage from "@/pages/ReportesPage";
import PagosPage from "@/pages/PagosPage";
import VariantesPage from "@/pages/VariantesPage";
import EmpresaPage from "@/pages/EmpresaPage";
import PanelStockPage from "@/pages/PanelStockPage";
import MisComisionesPage from "@/pages/MisComisionesPage";
import LiquidacionComisionesPage from "@/pages/LiquidacionComisionesPage";
import RentabilidadPage from "@/pages/RentabilidadPage";
import LegacyInventoryPage from "@/pages/LegacyInventoryPage";
import SistemaPage from "@/pages/SistemaPage";
import ReglasProntoPagoPage from "@/pages/ReglasProntoPagoPage";
import GastosMensualesPage from "@/pages/GastosMensualesPage";
import SociosPage from "@/pages/SociosPage";
import PedidosHubPage from "@/pages/PedidosHubPage";
import PedidoMovilPage from "@/pages/PedidoMovilPage";
import ProductosHubPage from "@/pages/ProductosHubPage";
import ReglasHubPage from "@/pages/ReglasHubPage";
import FinanzasHubPage from "@/pages/FinanzasHubPage";
import ConfiguracionHubPage from "@/pages/ConfiguracionHubPage";
import CatalogoPublicoPage from "@/pages/CatalogoPublicoPage";
import AppShell from "@/components/AppShell";
import { NotifSuppressProvider } from "@/context/NotifSuppressContext";
import { api } from "@/lib/api";
import { startAutoPreloadScheduler } from "@/lib/autoPreload";

// Inicializa el scheduler de auto-precarga una sola vez al cargar el bundle.
// Verifica en cada tick si toca correr (usuario configuró hora y hay sesión válida).
if (typeof window !== "undefined" && !window.__beemSchedulerStarted) {
  window.__beemSchedulerStarted = true;
  startAutoPreloadScheduler(api);
}
import "@/App.css";

const Protected = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <NotifSuppressProvider>
      <AppShell>{children}</AppShell>
    </NotifSuppressProvider>
  );
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
};

const Home = () => {
  const { user } = useAuth();
  if (user?.role === "admin") return <DashboardPage />;
  // Rol operativo: bodeguero / dispatcher con permiso de despachos
  const perms = user?.permissions || [];
  if (perms.includes("despachos.gestionar") || perms.includes("despachos.tomar")) {
    return <OperacionesPage />;
  }
  return <MiDiaPage />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/c/:slug" element={<CatalogoPublicoPage />} />
          <Route path="/" element={<Protected><Home /></Protected>} />
          <Route path="/operaciones" element={<Protected><OperacionesPage /></Protected>} />
          <Route path="/director-comercial" element={<Navigate to="/pedidos?tab=director-comercial" replace />} />
          <Route path="/remisiones" element={<Navigate to="/pedidos?tab=remisiones" replace />} />
          <Route path="/pedidos" element={<Protected><PedidosHubPage /></Protected>} />
          <Route path="/pedidos/movil" element={<Protected><PedidoMovilPage /></Protected>} />
          <Route path="/clientes" element={<Protected><ClientesPage /></Protected>} />
          <Route path="/productos" element={<Protected><ProductosHubPage /></Protected>} />
          <Route path="/excepciones" element={<Navigate to="/reglas?tab=excepciones" replace />} />
          <Route path="/notificaciones" element={<Protected><NotificacionesPage /></Protected>} />
          <Route path="/reportes" element={<Navigate to="/pedidos?tab=reportes" replace />} />
          <Route path="/pagos" element={<Navigate to="/pedidos?tab=pagos" replace />} />
          <Route path="/variantes" element={<Protected><VariantesPage /></Protected>} />
          <Route path="/comisiones" element={<Protected adminOnly><ComisionesPage /></Protected>} />
          <Route path="/empresa" element={<Navigate to="/configuracion?tab=empresa" replace />} />
          <Route path="/panel-stock" element={<Navigate to="/productos?tab=bajo-stock" replace />} />
          <Route path="/mis-comisiones" element={<Protected><MisComisionesPage /></Protected>} />
          <Route path="/liquidacion-comisiones" element={<Protected adminOnly><LiquidacionComisionesPage /></Protected>} />
          <Route path="/rentabilidad" element={<Navigate to="/finanzas?tab=rentabilidad" replace />} />
          <Route path="/inventario-legacy" element={<Protected adminOnly><LegacyInventoryPage /></Protected>} />
          <Route path="/sistema" element={<Protected adminOnly><SistemaPage /></Protected>} />
          <Route path="/listas-precios" element={<Protected adminOnly><ListasPreciosPage /></Protected>} />
          <Route path="/reglas-pronto-pago" element={<Protected adminOnly><ReglasProntoPagoPage /></Protected>} />
          <Route path="/gastos-mensuales" element={<Protected adminOnly><GastosMensualesPage /></Protected>} />
          <Route path="/socios" element={<Protected adminOnly><SociosPage /></Protected>} />
          <Route path="/reglas" element={<Protected><ReglasHubPage /></Protected>} />
          <Route path="/finanzas" element={<Protected adminOnly><FinanzasHubPage /></Protected>} />
          <Route path="/configuracion" element={<Protected adminOnly><ConfiguracionHubPage /></Protected>} />
          <Route path="/equipo" element={<Protected adminOnly><TeamPage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
